import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, Users, Smile, Castle, Target, Shield, Flag, Play } from 'lucide-react';
type Language = 'es' | 'en' | 'pt';
const translations = {
  es: {
    title: 'Actualizaciones',
    titleHighlight: 'Principales',
    tabs: [{
      id: 'fps',
      icon: Zap,
      name: '60 FPS Siempre',
      title: '60 FPS Siempre',
      description: 'El rendimiento del juego ha sido optimizado para ofrecer una experiencia fluida en todo momento. Incluso en situaciones exigentes como combates masivos o eventos intensos, el sistema se mantiene estable sin caídas bruscas de rendimiento.',
      description2: 'Se han implementado mejoras que reducen la carga del cliente y optimizan el uso de recursos, permitiendo una ejecución más eficiente en diferentes tipos de equipos. El resultado es una jugabilidad más rápida, visualmente más suave y consistente en cualquier escenario.',
      benefits: ['Rendimiento estable durante combates y eventos', 'Reducción significativa de lag y congelamientos', 'Experiencia fluida incluso en equipos de bajos recursos'],
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      imageUrl: 'https://cdn.magicpatterns.com/uploads/aRsi1ifXKm5dyTMvirScMw/image.png'
    }, {
      id: 'guild',
      icon: Users,
      name: 'Eventos y Premios',
      title: 'Eventos y Premios',
      description: 'El servidor ofrece eventos automatizados y manuales que mantienen la actividad constante durante todo el día. Desde invasiones hasta enfrentamientos estratégicos, siempre habrá algo en juego.',
      description2: 'Los eventos están diseñados para fomentar la competencia, la participación y la interacción entre jugadores, manteniendo el servidor activo y en evolución. Cada evento representa una oportunidad para avanzar, competir y ganar premios en Vanguard Coin.',
      benefits: ['Eventos activos durante todo el día', 'Participación competitiva entre jugadores', 'Recompensas en Vanguard Coin (USDT)'],
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      imageUrl: 'https://cdn.magicpatterns.com/uploads/3yfiwXD2pDsReGQgPRorgL/image.png'
    }, {
      id: 'interface',
      icon: Smile,
      name: 'Interfaz Optimizada',
      title: 'Interfaz Optimizada',
      description: 'La interfaz del juego ha sido potenciada y perfeccionada para ofrecer una experiencia más clara, intuitiva y eficiente. Cada elemento ha sido organizado cuidadosamente para facilitar el acceso a las funciones principales sin saturar la pantalla.',
      description2: 'El diseño prioriza la usabilidad, permitiendo a los jugadores interactuar con el sistema de forma rápida y natural, reduciendo bugs y mejorando el control general del juego. Tanto jugadores nuevos como experimentados podrán adaptarse fácilmente a una interfaz pensada para mejorar la experiencia en cada sesión.',
      benefits: ['Navegación rápida y accesible', 'Interfaz limpia y enfocada en la acción', 'Mayor comodidad durante largas sesiones de juego'],
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      imageUrl: 'https://cdn.magicpatterns.com/uploads/892Ni17Ag5VqnEi6oWHJfZ/image.png'
    }, {
      id: 'dungeons',
      icon: Castle,
      name: 'Mazmorras y Tablas',
      title: 'Mazmorras y Tablas de Clasificación',
      description: 'En el juego, puedes entrar a mazmorras para ganar equipo y recursos útiles. Mayor dificultad de mazmorra da mejores y más valiosas recompensas.',
      description2: 'Puedes competir con otros jugadores por tiempo claro y mostrar tu habilidad. Los mejores resultados aparecen en la tabla de clasificación, donde cada segundo cuenta. El servidor también tiene diferentes tablas de clasificación de actividad.',
      benefits: ['Mazmorras con diferentes niveles de dificultad', 'Mejores recompensas por desafíos más difíciles', 'Competencia basada en tiempo con otros jugadores'],
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      imageUrl: 'https://cdn.magicpatterns.com/uploads/cLt8ag1rvr61JwvG5zcPih/image.png'
    }, {
      id: 'quests',
      icon: Target,
      name: 'Sistema de Bosses',
      title: 'Enfrentamientos Épicos. Recompensas Competitivas.',
      description: 'El servidor cuenta con bosses globales diseñados para desafiar a todos los jugadores. Estos encuentros requieren preparación, estrategia y coordinación para ser dominados.',
      description2: 'Cada aparición genera competencia directa entre jugadores y clanes, convirtiendo cada batalla en un momento clave dentro del juego. Los enfrentamientos no solo son desafiantes, sino también altamente recompensantes.',
      benefits: ['Eventos PvE de alto impacto', 'Competencia directa entre jugadores', 'Recompensas valiosas y disputadas'],
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      imageUrl: 'https://cdn.magicpatterns.com/uploads/aRsi1ifXKm5dyTMvirScMw/image.png'
    }, {
      id: 'soul',
      icon: Shield,
      name: 'Dominio de Clanes',
      title: 'Poder, Lealtad y Control del Territorio.',
      description: 'El sistema de clanes está diseñado para fomentar la organización, la estrategia y la competencia a gran escala. Forma alianzas, lidera tu propio clan o únete a una fuerza dominante para competir por el control dentro del servidor.',
      description2: 'Cada clan representa más que un grupo: es una identidad, una jerarquía y una oportunidad de destacar en eventos clave como guerras, asedios y enfrentamientos masivos. La coordinación y el trabajo en equipo serán determinantes para alcanzar la supremacía.',
      benefits: ['Organización estratégica entre jugadores', 'Participación en guerras y eventos de alto nivel', 'Competencia real por dominio dentro del servidor'],
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      imageUrl: 'https://cdn.magicpatterns.com/uploads/3yfiwXD2pDsReGQgPRorgL/image.png'
    }]
  },
  en: {
    title: 'Major',
    titleHighlight: 'Updates',
    tabs: [{
      id: 'fps',
      icon: Zap,
      name: '60 FPS Always',
      title: '60 FPS Always',
      description: 'Game performance has been optimized to deliver a smooth experience at all times. Even in demanding situations like massive battles or intense events, the system remains stable without sudden performance drops.',
      description2: 'Improvements have been implemented that reduce client load and optimize resource usage, allowing more efficient execution on different types of equipment. The result is faster, visually smoother, and consistent gameplay in any scenario.',
      benefits: ['Stable performance during battles and events', 'Significant reduction in lag and freezing', 'Smooth experience even on low-resource equipment'],
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      imageUrl: 'https://cdn.magicpatterns.com/uploads/aRsi1ifXKm5dyTMvirScMw/image.png'
    }, {
      id: 'guild',
      icon: Users,
      name: 'Events & Rewards',
      title: 'Events and Rewards',
      description: 'The server offers automated and manual events that keep activity constant throughout the day. From invasions to strategic confrontations, there will always be something at stake.',
      description2: 'Events are designed to foster competition, participation, and interaction between players, keeping the server active and evolving. Each event represents an opportunity to advance, compete, and win Vanguard Coin rewards.',
      benefits: ['Active events throughout the day', 'Competitive participation between players', 'Vanguard Coin rewards (USDT)'],
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      imageUrl: 'https://cdn.magicpatterns.com/uploads/3yfiwXD2pDsReGQgPRorgL/image.png'
    }, {
      id: 'interface',
      icon: Smile,
      name: 'Optimized Interface',
      title: 'Optimized Interface',
      description: 'The game interface has been enhanced and refined to offer a clearer, more intuitive, and efficient experience. Each element has been carefully organized to facilitate access to main functions without cluttering the screen.',
      description2: 'The design prioritizes usability, allowing players to interact with the system quickly and naturally, reducing bugs and improving overall game control. Both new and experienced players will easily adapt to an interface designed to enhance the experience in every session.',
      benefits: ['Fast and accessible navigation', 'Clean interface focused on action', 'Greater comfort during long gaming sessions'],
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      imageUrl: 'https://cdn.magicpatterns.com/uploads/892Ni17Ag5VqnEi6oWHJfZ/image.png'
    }, {
      id: 'dungeons',
      icon: Castle,
      name: 'Dungeons & Boards',
      title: 'Dungeons and Leaderboards',
      description: 'In the game, you can enter dungeons to earn gear and useful resources. Higher dungeon difficulty gives better and more valuable rewards.',
      description2: 'You can compete with other players by clear time and show your skill. The best results appear on the leaderboard, where every second matters. The server also has different activity leaderboards.',
      benefits: ['Dungeons with different difficulty levels', 'Better rewards for harder challenges', 'Time-based competition with other players'],
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      imageUrl: 'https://cdn.magicpatterns.com/uploads/cLt8ag1rvr61JwvG5zcPih/image.png'
    }, {
      id: 'quests',
      icon: Target,
      name: 'Boss System',
      title: 'Epic Encounters. Competitive Rewards.',
      description: 'The server features global bosses designed to challenge all players. These encounters require preparation, strategy, and coordination to be mastered.',
      description2: 'Each appearance generates direct competition between players and clans, turning every battle into a key moment within the game. The confrontations are not only challenging but also highly rewarding.',
      benefits: ['High-impact PvE events', 'Direct competition between players', 'Valuable and contested rewards'],
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      imageUrl: 'https://cdn.magicpatterns.com/uploads/aRsi1ifXKm5dyTMvirScMw/image.png'
    }, {
      id: 'soul',
      icon: Shield,
      name: 'Clan Dominance',
      title: 'Power, Loyalty and Territory Control.',
      description: 'The clan system is designed to foster organization, strategy, and large-scale competition. Form alliances, lead your own clan, or join a dominant force to compete for control within the server.',
      description2: 'Each clan represents more than a group: it is an identity, a hierarchy, and an opportunity to stand out in key events such as wars, sieges, and massive confrontations. Coordination and teamwork will be decisive in achieving supremacy.',
      benefits: ['Strategic organization among players', 'Participation in wars and high-level events', 'Real competition for dominance within the server'],
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      imageUrl: 'https://cdn.magicpatterns.com/uploads/3yfiwXD2pDsReGQgPRorgL/image.png'
    }]
  },
  pt: {
    title: 'Atualizações',
    titleHighlight: 'Principais',
    tabs: [{
      id: 'fps',
      icon: Zap,
      name: '60 FPS Sempre',
      title: '60 FPS Sempre',
      description: 'O desempenho do jogo foi otimizado para oferecer uma experiência fluida em todos os momentos. Mesmo em situações exigentes como combates massivos ou eventos intensos, o sistema permanece estável sem quedas bruscas de desempenho.',
      description2: 'Foram implementadas melhorias que reduzem a carga do cliente e otimizam o uso de recursos, permitindo uma execução mais eficiente em diferentes tipos de equipamentos. O resultado é uma jogabilidade mais rápida, visualmente mais suave e consistente em qualquer cenário.',
      benefits: ['Desempenho estável durante combates e eventos', 'Redução significativa de lag e congelamentos', 'Experiência fluida mesmo em equipamentos de baixos recursos'],
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      imageUrl: 'https://cdn.magicpatterns.com/uploads/aRsi1ifXKm5dyTMvirScMw/image.png'
    }, {
      id: 'guild',
      icon: Users,
      name: 'Eventos e Prêmios',
      title: 'Eventos e Prêmios',
      description: 'O servidor oferece eventos automatizados e manuais que mantêm a atividade constante durante todo o dia. Desde invasões até confrontos estratégicos, sempre haverá algo em jogo.',
      description2: 'Os eventos são projetados para promover a competição, participação e interação entre jogadores, mantendo o servidor ativo e em evolução. Cada evento representa uma oportunidade para avançar, competir e ganhar prêmios em Vanguard Coin.',
      benefits: ['Eventos ativos durante todo o dia', 'Participação competitiva entre jogadores', 'Recompensas em Vanguard Coin (USDT)'],
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      imageUrl: 'https://cdn.magicpatterns.com/uploads/3yfiwXD2pDsReGQgPRorgL/image.png'
    }, {
      id: 'interface',
      icon: Smile,
      name: 'Interface Otimizada',
      title: 'Interface Otimizada',
      description: 'A interface do jogo foi aprimorada e aperfeiçoada para oferecer uma experiência mais clara, intuitiva e eficiente. Cada elemento foi organizado cuidadosamente para facilitar o acesso às funções principais sem sobrecarregar a tela.',
      description2: 'O design prioriza a usabilidade, permitindo que os jogadores interajam com o sistema de forma rápida e natural, reduzindo bugs e melhorando o controle geral do jogo. Tanto jogadores novos quanto experientes poderão se adaptar facilmente a uma interface pensada para melhorar a experiência em cada sessão.',
      benefits: ['Navegação rápida e acessível', 'Interface limpa e focada na ação', 'Maior conforto durante longas sessões de jogo'],
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      imageUrl: 'https://cdn.magicpatterns.com/uploads/892Ni17Ag5VqnEi6oWHJfZ/image.png'
    }, {
      id: 'dungeons',
      icon: Castle,
      name: 'Masmorras e Tabelas',
      title: 'Masmorras e Tabelas de Classificação',
      description: 'No jogo, você pode entrar em masmorras para ganhar equipamentos e recursos úteis. Maior dificuldade de masmorra dá melhores e mais valiosas recompensas.',
      description2: 'Você pode competir com outros jogadores por tempo claro e mostrar sua habilidade. Os melhores resultados aparecem na tabela de classificação, onde cada segundo conta. O servidor também tem diferentes tabelas de classificação de atividade.',
      benefits: ['Masmorras com diferentes níveis de dificuldade', 'Melhores recompensas por desafios mais difíceis', 'Competição baseada em tempo com outros jogadores'],
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      imageUrl: 'https://cdn.magicpatterns.com/uploads/cLt8ag1rvr61JwvG5zcPih/image.png'
    }, {
      id: 'quests',
      icon: Target,
      name: 'Sistema de Bosses',
      title: 'Confrontos Épicos. Recompensas Competitivas.',
      description: 'O servidor possui bosses globais projetados para desafiar todos os jogadores. Esses encontros exigem preparação, estratégia e coordenação para serem dominados.',
      description2: 'Cada aparição gera competição direta entre jogadores e clãs, transformando cada batalha em um momento chave dentro do jogo. Os confrontos não são apenas desafiadores, mas também altamente recompensadores.',
      benefits: ['Eventos PvE de alto impacto', 'Competição direta entre jogadores', 'Recompensas valiosas e disputadas'],
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      imageUrl: 'https://cdn.magicpatterns.com/uploads/aRsi1ifXKm5dyTMvirScMw/image.png'
    }, {
      id: 'soul',
      icon: Shield,
      name: 'Domínio de Clãs',
      title: 'Poder, Lealdade e Controle do Território.',
      description: 'O sistema de clãs foi projetado para promover a organização, estratégia e competição em grande escala. Forme alianças, lidere seu próprio clã ou junte-se a uma força dominante para competir pelo controle dentro do servidor.',
      description2: 'Cada clã representa mais do que um grupo: é uma identidade, uma hierarquia e uma oportunidade de se destacar em eventos-chave como guerras, cercos e confrontos massivos. A coordenação e o trabalho em equipe serão decisivos para alcançar a supremacia.',
      benefits: ['Organização estratégica entre jogadores', 'Participação em guerras e eventos de alto nível', 'Competição real por domínio dentro do servidor'],
      videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
      imageUrl: 'https://cdn.magicpatterns.com/uploads/3yfiwXD2pDsReGQgPRorgL/image.png'
    }]
  }
};
export function Updates() {
  const [language, setLanguage] = useState<Language>('es');
  const [activeTab, setActiveTab] = useState('fps');
  const t = translations[language];
  const activeContent = t.tabs.find(tab => tab.id === activeTab)!;
  useEffect(() => {
    const savedLang = localStorage.getItem('language') as Language;
    if (savedLang) setLanguage(savedLang);
    const handleLanguageChange = (e: CustomEvent<Language>) => {
      setLanguage(e.detail);
    };
    window.addEventListener('languageChange', handleLanguageChange as EventListener);
    return () => window.removeEventListener('languageChange', handleLanguageChange as EventListener);
  }, []);
  return <section className="py-16 md:py-24 relative z-10 bg-darkerBlue" data-id="element-415">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-416">
        {/* Header */}
        <div className="text-center mb-12" data-id="element-417">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4" data-id="element-418">
            {t.title}{' '}
            <span className="text-gradient-gold" data-id="element-419">{t.titleHighlight}</span>
          </motion.h2>
          <motion.div initial={{
          opacity: 0,
          scale: 0
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} className="w-24 h-1 bg-gold mx-auto rounded-full" data-id="element-420" />
        </div>

        {/* Tabs Navigation */}
        <div className="mb-8 overflow-x-auto" data-id="element-421">
          <div className="flex gap-2 min-w-max md:min-w-0 md:grid md:grid-cols-3 lg:grid-cols-6" data-id="element-422">
            {t.tabs.map(tab => {
            const Icon = tab.icon;
            return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-300 whitespace-nowrap ${activeTab === tab.id ? 'bg-gradient-to-r from-gold/20 to-yellow-600/20 border-2 border-gold text-white' : 'bg-darkerBlue border-2 border-white/10 text-gray-400 hover:border-gold/50 hover:text-white'}`} data-id="element-423">
                  <Icon className="w-5 h-5 flex-shrink-0" data-id="element-424" />
                  <span className="font-semibold text-sm" data-id="element-425">{tab.name}</span>
                </button>;
          })}
          </div>
        </div>

        {/* Content Area */}
        <AnimatePresence mode="wait" data-id="element-426">
          <motion.div key={activeTab} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -20
        }} transition={{
          duration: 0.3
        }} className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start" data-id="element-427">
            {/* Left: Text Content */}
            <div className="space-y-6" data-id="element-428">
              <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white" data-id="element-429">
                {activeContent.title}
              </h3>

              <p className="text-gray-300 leading-relaxed" data-id="element-430">
                {activeContent.description}
              </p>

              <p className="text-gray-300 leading-relaxed" data-id="element-431">
                {activeContent.description2}
              </p>

              <div className="pt-4" data-id="element-432">
                <h4 className="text-gold font-bold text-lg mb-4 font-cinzel" data-id="element-433">
                  {language === 'es' ? 'Beneficios clave:' : language === 'en' ? 'Key benefits:' : 'Benefícios principais:'}
                </h4>
                <ul className="space-y-3" data-id="element-434">
                  {activeContent.benefits.map((benefit, index) => <li key={index} className="flex items-start gap-3 text-gray-300" data-id="element-435">
                      <div className="w-2 h-2 rounded-full bg-gold mt-2 flex-shrink-0" data-id="element-436" />
                      <span data-id="element-437">{benefit}</span>
                    </li>)}
                </ul>
              </div>
            </div>

            {/* Right: Video/Image */}
            <div className="rounded-2xl overflow-hidden border border-white/10 relative group bg-black" data-id="element-438">
              <div className="aspect-video relative flex items-center justify-center overflow-hidden rounded-lg" data-id="element-439">
                {/* Background Video */}
                <video src="https://cdn.magicpatterns.com/uploads/oS1z4iBhZNr8sDusM9y1WT/Video%20333%20(1)%20-%20Trim.mp4" autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover" data-id="element-440" />
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>;
}